/**
 * NotificationToast — renders proactive-job notifications as sliding toasts.
 *
 * Shows in the top-right corner. Each toast auto-dismisses after 15 seconds
 * or on click. Also exports a small bell badge for the sidebar/header.
 */

import { useEffect, useRef, useState } from "react";
import type { ProactiveNotification } from "@/hooks/useNotifications";
import { getIdToken } from "@/auth/cognito";
import styles from "./NotificationToast.module.css";

const AUTO_DISMISS_MS = 15_000;

interface ToastProps {
  notifications: ProactiveNotification[];
  onDismiss: (id: string) => void;
  onClick?: (n: ProactiveNotification) => void;
}

type ReconfirmAction = "reconfirm" | "snooze" | "silence";

async function postReconfirm(jobId: string, action: ReconfirmAction) {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const body =
    action === "snooze" ? JSON.stringify({ days: 14 }) : undefined;
  const res = await fetch(`/api/proactive/jobs/${jobId}/${action}`, {
    method: "POST",
    headers,
    body,
  });
  if (!res.ok) {
    throw new Error(`${res.status}: ${(await res.text()).slice(0, 200)}`);
  }
  return res.json();
}

function ReconfirmChip({
  notification,
  onResolved,
}: {
  notification: ProactiveNotification;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState<ReconfirmAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handle = async (action: ReconfirmAction) => {
    setBusy(action);
    setError(null);
    try {
      await postReconfirm(notification.jobId, action);
      onResolved();
    } catch (e: any) {
      setError(e.message ?? "failed");
      setBusy(null);
    }
  };

  return (
    <div className={styles.reconfirmRow} onClick={(e) => e.stopPropagation()}>
      <div className={styles.reconfirmPrompt}>
        Foreman noticed you've been ignoring this — still useful?
      </div>
      <div className={styles.reconfirmActions}>
        <button
          className={`${styles.reconfirmBtn} ${styles.reconfirmBtnPrimary}`}
          disabled={busy !== null}
          onClick={() => handle("reconfirm")}
        >
          {busy === "reconfirm" ? "…" : "Yes, keep it"}
        </button>
        <button
          className={styles.reconfirmBtn}
          disabled={busy !== null}
          onClick={() => handle("snooze")}
          title="Pause for 2 weeks, then resume"
        >
          {busy === "snooze" ? "…" : "Snooze 2w"}
        </button>
        <button
          className={styles.reconfirmBtn}
          disabled={busy !== null}
          onClick={() => handle("silence")}
        >
          {busy === "silence" ? "…" : "No, stop"}
        </button>
      </div>
      {error && (
        <div style={{ color: "#c43030", fontSize: 11 }}>{error}</div>
      )}
    </div>
  );
}

function formatTime(ts: number) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function cleanJobName(name: string): string {
  return name
    .toLowerCase()
    .replace(/^(please |can you |could you )/, "")
    .replace(/\?+$/, "")
    .replace(/^\w/, (c) => c.toUpperCase())
    .slice(0, 48);
}

/** Strip markdown syntax so the toast preview shows plain prose, not a
 *  miniaturised table or heading. Keeps the first meaningful line or two. */
function toPlainPreview(text: string, max = 120): string {
  const stripped = text
    // drop markdown tables entirely
    .replace(/^\|.*\|\s*$/gm, "")
    .replace(/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/gm, "")
    // bold/italic/code markers
    .replace(/\*\*/g, "")
    .replace(/(^|\s)[*_](\S.*?\S)[*_]($|\s)/g, "$1$2$3")
    .replace(/`([^`]+)`/g, "$1")
    // headings / blockquotes / list bullets
    .replace(/^#+\s+/gm, "")
    .replace(/^>\s?/gm, "")
    .replace(/^[-*+]\s+/gm, "")
    // collapse whitespace
    .replace(/\n{2,}/g, " — ")
    .replace(/\s+/g, " ")
    .trim();
  if (stripped.length <= max) return stripped;
  return stripped.slice(0, max).replace(/\s+\S*$/, "") + "…";
}

export function NotificationToasts({
  notifications,
  onDismiss,
  onClick,
}: ToastProps) {
  const visible = notifications.filter((n) => !n.dismissed).slice(0, 5);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Auto-dismiss after 15s — but pin reconfirm prompts so the user
  // actually sees them.
  //
  // Targeted cleanup: only clear timers for notifications that are no
  // longer visible (or that are now reconfirm-required). Previously we
  // cleared ALL timers on every effect run, which meant a notification
  // that had been on screen for 14s would have its timer reset to a
  // fresh 15s the moment any *other* notification arrived or was
  // dismissed — so visible toasts could pile up far beyond the
  // intended dismiss window.
  useEffect(() => {
    const visibleIds = new Set<string>();
    for (const n of visible) {
      if (n.reconfirmRequired) continue;
      visibleIds.add(n.id);
      if (!timers.current.has(n.id)) {
        const t = setTimeout(() => {
          onDismiss(n.id);
          timers.current.delete(n.id);
        }, AUTO_DISMISS_MS);
        timers.current.set(n.id, t);
      }
    }
    // Cancel timers for ids that have left the visible set
    for (const [id, t] of timers.current) {
      if (!visibleIds.has(id)) {
        clearTimeout(t);
        timers.current.delete(id);
      }
    }
    // No cleanup that nukes everything — only on full unmount.
    // (Captured via a ref outside this effect's closure.)
  }, [visible, onDismiss]);

  // True full-unmount cleanup — clears any remaining timers.
  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  if (visible.length === 0) return null;

  return (
    <div className={styles.toastContainer}>
      {visible.map((n) => (
        <div
          key={n.id}
          className={styles.toast}
          style={n.emailData ? { cursor: "pointer" } : undefined}
          onClick={() => {
            if (n.reconfirmRequired) return;
            onClick?.(n);
            onDismiss(n.id);
          }}
        >
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.metaRow}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <span>Foreman</span>
                <span className={styles.metaDot}>·</span>
                <span>{formatTime(n.receivedAt)}</span>
              </div>
              <span className={styles.jobName}>
                {n.emailData ? "New email" : cleanJobName(n.jobName)}
              </span>
            </div>
            <button
              className={styles.dismiss}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(n.id);
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <div className={styles.text}>
            {n.emailData
              ? `${n.emailData.from} — ${n.emailData.subject}`
              : toPlainPreview(n.text)}
          </div>
          {!n.reconfirmRequired && (
            <div className={styles.viewCta}>
              {n.emailData ? "Review draft →" : "Open in chat →"}
            </div>
          )}
          {n.reconfirmRequired && n.jobId && (
            <ReconfirmChip
              notification={n}
              onResolved={() => onDismiss(n.id)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/** Small bell badge for sidebar/header — shows unread count */
export function NotificationBell({
  count,
  onClick,
}: {
  count: number;
  onClick?: () => void;
}) {
  return (
    <div className={styles.bellWrapper} onClick={onClick} title="Notifications">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {count > 0 && (
        <span className={styles.badge}>{count > 9 ? "9+" : count}</span>
      )}
    </div>
  );
}
